/* eslint-disable react/no-unescaped-entities */
import React from 'react';

const PrivacyPolicy = () => {
    return (
        <div className={'container mx-auto prose max-w-4xl mt-6 pb-12'}>
            <div data-custom-class="body">
                <div>
                    <strong>PRIVACY POLICY</strong>
                </div>
                <div>
                    <strong>Last updated Oct 20, 2023</strong>
                </div>
                <div>
                    This privacy notice for HUDDA d.o.o. ("<strong>Company</strong>," "<strong>we</strong>," "
                    <strong>us</strong>," or "<strong>our</strong>"), describes how and why we might collect, store,
                    use, and/or share ("<strong>process</strong>") your information when you use our services ("
                    <strong>Services</strong>"), such as when you:
                </div>
                <ul>
                    <li>
                        Visit our website at{' '}
                        <a href="https://kamzavikend.si" target="_blank" data-custom-class="link" rel="noreferrer">
                            https://kamzavikend.si
                        </a>
                        , or any website of ours that links to this privacy notice
                    </li>
                </ul>
                <div>
                    <ul>
                        <li>Engage with us in other related ways, including any sales, marketing, or events</li>
                    </ul>
                    <div>
                        <strong>Questions or concerns?&nbsp;</strong>Reading this privacy notice will help you
                        understand your privacy rights and choices. If you do not agree with our policies and practices,
                        please do not use our Services. If you still have any questions or concerns, please contact us
                        at info@kamzavikend.si.
                    </div>
                    <div>
                        <strong>SUMMARY OF KEY POINTS</strong>
                    </div>
                    <div>
                        <strong>
                            <em>
                                This summary provides key points from our privacy notice, but you can find out more
                                details about any of these topics by clicking the link following each key point or by
                                using our&nbsp;
                            </em>
                        </strong>
                        <a href="#toc" data-custom-class="link">
                            <strong>
                                <em>table of contents</em>
                            </strong>
                        </a>
                        <strong>
                            <em>&nbsp;below to find the section you are looking for.</em>
                        </strong>
                    </div>
                    <div>
                        <strong>What personal information do we process?</strong> When you visit, use, or navigate our
                        Services, we may process personal information depending on how you interact with HUDDA d.o.o.
                        and the Services, the choices you make, and the products and features you use. Learn more
                        about&nbsp;
                        <a href="#personalinfo" data-custom-class="link">
                            personal information you disclose to us
                        </a>
                        .
                    </div>
                    <div>
                        <strong>Do we process any sensitive personal information?</strong> We do not process sensitive
                        personal information.
                    </div>
                    <div>
                        <strong>Do we receive any information from third parties?</strong> We do not receive any
                        information from third parties.
                    </div>
                    <div>
                        <strong>How do we process your information?</strong> We process your information to provide,
                        improve, and administer our Services, communicate with you, for security and fraud prevention,
                        and to comply with law. We may also process your information for other purposes with your
                        consent. We process your information only when we have a valid legal reason to do so. Learn more
                        about&nbsp;
                        <a href="#infouse" data-custom-class="link">
                            how we process your information
                        </a>
                        .
                    </div>
                    <div>
                        <strong>
                            In what situations and with which types of parties do we share personal information?
                        </strong>{' '}
                        We may share information in specific situations and with specific categories of third parties.
                        Learn more about&nbsp;
                        <a href="#whoshare" data-custom-class="link">
                            when and with whom we share your personal information
                        </a>
                        .
                    </div>
                    <div>
                        <strong>How do we keep your information safe?</strong> We have organizational and technical
                        processes and procedures in place to protect your personal information. However, no electronic
                        transmission over the internet or information storage technology can be guaranteed to be 100%
                        secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized
                        third parties will not be able to defeat our security and improperly collect, access, steal, or
                        modify your information. Learn more about&nbsp;
                        <a href="#infosafe" data-custom-class="link">
                            how we keep your information safe
                        </a>
                        .
                    </div>
                    <div>
                        <strong>What are your rights?</strong> Depending on where you are located geographically, the
                        applicable privacy law may mean you have certain rights regarding your personal information.
                        Learn more about&nbsp;
                        <a href="#privacyrights" data-custom-class="link">
                            your privacy rights
                        </a>
                        .
                    </div>
                    <div>
                        <strong>How do you exercise your rights?</strong> The easiest way to exercise your rights is by
                        submitting a&nbsp;
                        <a
                            href="https://app.termly.io/notify/9e0e0db1-8123-425f-a1b2-0296e0251163"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-custom-class="link"
                        >
                            data subject access request
                        </a>
                        , or by contacting us. We will consider and act upon any request in accordance with applicable
                        data protection laws.
                    </div>
                    <div>
                        Want to learn more about what HUDDA d.o.o. does with any information we collect?&nbsp;
                        <a href="#toc" data-custom-class="link">
                            Review the privacy notice in full
                        </a>
                        .
                    </div>
                    <div id="toc">
                        <strong>TABLE OF CONTENTS</strong>
                    </div>
                    <div>
                        <a href="#infocollect" data-custom-class="link">
                            1. WHAT INFORMATION DO WE COLLECT?
                        </a>
                    </div>
                    <div>
                        <a href="#infouse" data-custom-class="link">
                            2. HOW DO WE PROCESS YOUR INFORMATION?
                        </a>
                    </div>
                    <div>
                        <a href="#legalbases" data-custom-class="link">
                            3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
                        </a>
                    </div>
                    <div>
                        <a href="#whoshare" data-custom-class="link">
                            4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
                        </a>
                    </div>
                    <div>
                        <a href="#3pwebsites">5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</a>
                    </div>
                    <div>
                        <a href="#cookies" data-custom-class="link">
                            6. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
                        </a>
                    </div>
                    <div>
                        <a href="#sociallogins" data-custom-class="link">
                            7. HOW DO WE HANDLE YOUR SOCIAL LOGINS?
                        </a>
                    </div>
                    <div>
                        <a href="#inforetain" data-custom-class="link">
                            8. HOW LONG DO WE KEEP YOUR INFORMATION?
                        </a>
                    </div>
                    <div>
                        <a href="#infosafe" data-custom-class="link">
                            9. HOW DO WE KEEP YOUR INFORMATION SAFE?
                        </a>
                    </div>
                    <div>
                        <a href="#infominors" data-custom-class="link">
                            10. DO WE COLLECT INFORMATION FROM MINORS?
                        </a>
                    </div>
                    <div>
                        <a href="#privacyrights" data-custom-class="link">
                            11. WHAT ARE YOUR PRIVACY RIGHTS?
                        </a>
                    </div>
                    <div>
                        <a href="#DNT" data-custom-class="link">
                            12. CONTROLS FOR DO-NOT-TRACK FEATURES
                        </a>
                    </div>
                    <div>
                        <a href="#caresidents" data-custom-class="link">
                            13. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
                        </a>
                    </div>
                    <div>
                        <a href="#policyupdates" data-custom-class="link">
                            14. DO WE MAKE UPDATES TO THIS NOTICE?
                        </a>
                    </div>
                    <div>
                        <a href="#contact" data-custom-class="link">
                            15. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                        </a>
                    </div>
                    <div>
                        <a href="#request" data-custom-class="link">
                            16. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
                        </a>
                    </div>
                    <div id="infocollect">
                        <strong>1. WHAT INFORMATION DO WE COLLECT?</strong>
                    </div>
                    <div id="personalinfo">
                        <strong>Personal information you disclose to us</strong>
                    </div>
                    <div>
                        <div>
                            <strong>
                                <em>In Short:</em>
                            </strong>
                            <em>We collect personal information that you provide to us.</em>
                        </div>
                    </div>
                    <div>
                        We collect personal information that you voluntarily provide to us when you register on the
                        Services,&nbsp;express an interest in obtaining information about us or our products and
                        Services, when you participate in activities on the Services, or otherwise when you contact us.
                    </div>
                    <div>
                        <strong>Personal Information Provided by You.</strong> The personal information that we collect
                        depends on the context of your interactions with us and the Services, the choices you make, and
                        the products and features you use. The personal information we collect may include the
                        following:
                    </div>
                    <ul>
                        <li>names</li>
                    </ul>
                    <ul>
                        <li>email addresses</li>
                    </ul>
                    <ul>
                        <li>usernames</li>
                    </ul>
                    <ul>
                        <li>passwords</li>
                    </ul>
                    <ul>
                        <li>contact preferences</li>
                    </ul>
                    <ul>
                        <li>contact or authentication data</li>
                    </ul>
                    <ul>
                        <li>google avatar</li>
                    </ul>
                    <ul>
                        <li>facebook avatar</li>
                    </ul>
                    <ul>
                        <li>google user id</li>
                    </ul>
                    <ul>
                        <li>facebook user id</li>
                    </ul>
                    <div id="sensitiveinfo">
                        <strong>Sensitive Information.</strong> We do not process sensitive information.
                    </div>
                    <div>
                        <strong>Social Media Login Data.&nbsp;</strong>We may provide you with the option to register
                        with us using your existing social media account details, like your Facebook, Twitter, or other
                        social media account. If you choose to register in this way, we will collect the information
                        described in the section called "
                        <a href="#sociallogins" data-custom-class="link">
                            HOW DO WE HANDLE YOUR SOCIAL LOGINS?
                        </a>
                        " below.
                    </div>
                    <div>
                        All personal information that you provide to us must be true, complete, and accurate, and you
                        must notify us of any changes to such personal information.
                    </div>
                    <div>
                        <strong>Information automatically collected</strong>
                    </div>
                    <div>
                        <div>
                            <strong>
                                <em>In Short:</em>
                            </strong>
                            <em>
                                Some information &mdash; such as your Internet Protocol (IP) address and/or browser and
                                device characteristics &mdash; is collected automatically when you visit our Services.
                            </em>
                        </div>
                    </div>
                    <div>
                        We automatically collect certain information when you visit, use, or navigate the Services. This
                        information does not reveal your specific identity (like your name or contact information) but
                        may include device and usage information, such as your IP address, browser and device
                        characteristics, operating system, language preferences, referring URLs, device name, country,
                        location, information about how and when you use our Services, and other technical information.
                        This information is primarily needed to maintain the security and operation of our Services, and
                        for our internal analytics and reporting purposes.
                    </div>
                    <div>
                        Like many businesses, we also collect information through cookies and similar technologies. You
                        can find out more about this in our Cookie Notice:{' '}
                        <a
                            href="https://kamzavikend.si/piskotki"
                            target="_blank"
                            data-custom-class="link"
                            rel="noreferrer"
                        >
                            https://kamzavikend.si/piskotki
                        </a>
                        .
                    </div>
                    <div>The information we collect includes:</div>
                    <ul>
                        <li>
                            <em>Log and Usage Data.</em> Log and usage data is service-related, diagnostic, usage, and
                            performance information our servers automatically collect when you access or use our
                            Services and which we record in log files. Depending on how you interact with us, this log
                            data may include your IP address, device information, browser type, and settings and
                            information about your activity in the Services&nbsp;(such as the date/time stamps
                            associated with your usage, pages and files viewed, searches, and other actions you take
                            such as which features you use), device event information (such as system activity, error
                            reports (sometimes called "crash dumps"), and hardware settings).
                        </li>
                    </ul>
                    <ul>
                        <li>
                            <em>Device Data.</em> We collect device data such as information about your computer, phone,
                            tablet, or other device you use to access the Services. Depending on the device used, this
                            device data may include information such as your IP address (or proxy server), device and
                            application identification numbers, location, browser type, hardware model, Internet service
                            provider and/or mobile carrier, operating system, and system configuration information.
                        </li>
                    </ul>
                    <ul>
                        <li>
                            <em>Location Data.</em> We collect location data such as information about your device's
                            location, which can be either precise or imprecise. How much information we collect depends
                            on the type and settings of the device you use to access the Services. For example, we may
                            use GPS and other technologies to collect geolocation data that tells us your current
                            location (based on your IP address). You can opt out of allowing us to collect this
                            information either by refusing access to the information or by disabling your Location
                            setting on your device. However, if you choose to opt out, you may not be able to use
                            certain aspects of the Services.
                        </li>
                    </ul>
                    <div id="infouse">
                        <strong>2. HOW DO WE PROCESS YOUR INFORMATION?</strong>
                    </div>
                    <div>
                        <div>
                            <strong>
                                <em>In Short:&nbsp;</em>
                            </strong>
                            <em>
                                We process your information to provide, improve, and administer our Services,
                                communicate with you, for security and fraud prevention, and to comply with law. We may
                                also process your information for other purposes with your consent.
                            </em>
                        </div>
                    </div>
                    <div>
                        <strong>
                            We process your personal information for a variety of reasons, depending on how you interact
                            with our Services, including:
                        </strong>
                    </div>
                    <ul>
                        <li>
                            <strong>
                                To facilitate account creation and authentication and otherwise manage user
                                accounts.&nbsp;
                            </strong>
                            We may process your information so you can create and log in to your account, as well as
                            keep your account in working order.
                        </li>
                    </ul>
                    <div>
                        <ul>
                            <li>
                                <strong>To request feedback.&nbsp;</strong>We may process your information when
                                necessary to request feedback and to contact you about your use of our Services.
                            </li>
                        </ul>
                        <div>
                            <ul>
                                <li>
                                    <strong>To send you marketing and promotional communications.&nbsp;</strong>
                                    We may process the personal information you send to us for our marketing purposes,
                                    if this is in accordance with your marketing preferences. You can opt out of our
                                    marketing emails at any time. For more information, see "
                                    <a href="#privacyrights" data-custom-class="link">
                                        WHAT ARE YOUR PRIVACY RIGHTS?
                                    </a>
                                    " below.
                                </li>
                            </ul>
                            <div>
                                <ul>
                                    <li>
                                        <strong>To identify usage trends.</strong> We may process information about how
                                        you use our Services to better understand how they are being used so we can
                                        improve them.
                                    </li>
                                </ul>
                                <div>
                                    <ul>
                                        <li>
                                            <strong>To save or protect an individual's vital interest.</strong> We may
                                            process your information when necessary to save or protect an
                                            individual&rsquo;s vital interest, such as to prevent harm.
                                        </li>
                                    </ul>
                                    <ul>
                                        <li>
                                            <strong>To show relevant content</strong>
                                            <strong>.</strong> We may use your location to show relevant POIs and trip
                                            ideas that are relavant to you.
                                        </li>
                                    </ul>
                                    <div id="legalbases">
                                        <strong>3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?</strong>
                                    </div>
                                    <div>
                                        <em>
                                            <strong>In Short:&nbsp;</strong>
                                            We only process your personal information when we believe it is necessary
                                            and we have a valid legal reason (i.e., legal basis) to do so under
                                            applicable law, like with your consent, to comply with laws, to provide you
                                            with services to enter into or fulfill our contractual obligations, to
                                            protect your rights, or to fulfill our legitimate business interests.
                                        </em>
                                    </div>
                                    <div>
                                        The General Data Protection Regulation (GDPR) and UK GDPR require us to explain
                                        the valid legal bases we rely on in order to process your personal information.
                                        As such, we may rely on the following legal bases to process your personal
                                        information:
                                    </div>
                                    <ul>
                                        <li>
                                            <strong>Consent.&nbsp;</strong>
                                            We may process your information if you have given us permission (i.e.,
                                            consent) to use your personal information for a specific purpose. You can
                                            withdraw your consent at any time. Learn more about&nbsp;
                                            <a href="#withdrawconsent" data-custom-class="link">
                                                withdrawing your consent
                                            </a>
                                            .
                                        </li>
                                    </ul>
                                    <div>
                                        <ul>
                                            <li>
                                                <strong>Legitimate Interests.</strong> We may process your information
                                                when we believe it is reasonably necessary to achieve our legitimate
                                                business interests and those interests do not outweigh your interests
                                                and fundamental rights and freedoms. For example, we may process your
                                                personal information for some of the purposes described in order to:
                                            </li>
                                        </ul>
                                        <ul>
                                            <li>
                                                Send users information about special offers and discounts on our
                                                products and services
                                            </li>
                                        </ul>
                                        <div>
                                            <ul>
                                                <li>
                                                    Analyze how our Services are used so we can improve them to engage
                                                    and retain users
                                                </li>
                                            </ul>
                                            <div>
                                                <div>
                                                    <ul>
                                                        <li>
                                                            Understand how our users use our products and services so we
                                                            can improve user experience
                                                        </li>
                                                    </ul>
                                                    <ul>
                                                        <li>In order to improve their experience</li>
                                                    </ul>
                                                    <ul>
                                                        <li>
                                                            <strong>Legal Obligations.</strong> We may process your
                                                            information where we believe it is necessary for compliance
                                                            with our legal obligations, such as to cooperate with a law
                                                            enforcement body or regulatory agency, exercise or defend
                                                            our legal rights, or disclose your information as evidence
                                                            in litigation in which we are involved.
                                                        </li>
                                                    </ul>
                                                    <ul>
                                                        <li>
                                                            <strong>Vital Interests.</strong> We may process your
                                                            information where we believe it is necessary to protect your
                                                            vital interests or the vital interests of a third party,
                                                            such as situations involving potential threats to the safety
                                                            of any person.
                                                        </li>
                                                    </ul>
                                                    <div id="whoshare">
                                                        <strong>
                                                            4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
                                                        </strong>
                                                    </div>
                                                    <div>
                                                        <strong>
                                                            <em>In Short:</em>
                                                        </strong>
                                                        <em>
                                                            &nbsp;We may share information in specific situations
                                                            described in this section and/or with the following
                                                            categories of third parties.
                                                        </em>
                                                    </div>
                                                    <div>
                                                        <strong>
                                                            Vendors, Consultants, and Other Third-Party Service
                                                            Providers.
                                                        </strong>{' '}
                                                        We may share your data with third-party vendors, service
                                                        providers, contractors, or agents ("
                                                        <strong>third parties</strong>
                                                        ") who perform services for us or on our behalf and require
                                                        access to such information to do that work. We have contracts in
                                                        place with our third parties, which are designed to help
                                                        safeguard your personal information. This means that they cannot
                                                        do anything with your personal information unless we have
                                                        instructed them to do it. They will also not share your personal
                                                        information with any organization apart from us. They also
                                                        commit to protect the data they hold on our behalf and to retain
                                                        it for the period we instruct. The categories of third parties
                                                        we may share personal information with are as follows:
                                                    </div>
                                                    <ul>
                                                        <li>Data Analytics Services</li>
                                                    </ul>
                                                    <ul>
                                                        <li>Cloud Computing Services</li>
                                                    </ul>
                                                    <ul>
                                                        <li>Data Storage Service Providers</li>
                                                    </ul>
                                                    <div>
                                                        We also may need to share your personal information in the
                                                        following situations:
                                                    </div>
                                                    <ul>
                                                        <li>
                                                            <strong>Business Transfers.</strong> We may share or
                                                            transfer your information in connection with, or during
                                                            negotiations of, any merger, sale of company assets,
                                                            financing, or acquisition of all or a portion of our
                                                            business to another company.
                                                        </li>
                                                    </ul>
                                                    <div>
                                                        <div>
                                                            <strong>
                                                                5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?
                                                            </strong>
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <em>In Short:</em>
                                                            </strong>
                                                            <em>
                                                                &nbsp;We are not responsible for the safety of any
                                                                information that you share with third parties that we
                                                                may link to or who advertise on our Services, but are
                                                                not affiliated with, our Services.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            The Services may link to third-party websites, online
                                                            services, or mobile applications and/or contain
                                                            advertisements from third parties that are not affiliated
                                                            with us and which may link to other websites, services, or
                                                            applications. Accordingly, we do not make any guarantee
                                                            regarding any such third parties, and we will not be liable
                                                            for any loss or damage caused by the use of such third-party
                                                            websites, services, or applications. The inclusion of a link
                                                            towards a third-party website, service, or application does
                                                            not imply an endorsement by us. We cannot guarantee the
                                                            safety and privacy of data you provide to any third parties.
                                                            Any data collected by third parties is not covered by this
                                                            privacy notice. We are not responsible for the content or
                                                            privacy and security practices and policies of any third
                                                            parties, including other websites, services, or applications
                                                            that may be linked to or from the Services. You should
                                                            review the policies of such third parties and contact them
                                                            directly to respond to your questions.
                                                        </div>
                                                        <div id="cookies">
                                                            <strong>
                                                                6. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
                                                            </strong>
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <em>In Short:</em>
                                                            </strong>
                                                            <em>
                                                                &nbsp;We may use cookies and other tracking technologies
                                                                to collect and store your information.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            We may use cookies and similar tracking technologies (like
                                                            web beacons and pixels) to access or store information.
                                                            Specific information about how we use such technologies and
                                                            how you can refuse certain cookies is set out in our Cookie
                                                            Notice:{' '}
                                                            <a
                                                                href="https://kamzavikend.si/piskotki"
                                                                target="_blank"
                                                                data-custom-class="link"
                                                                rel="noreferrer"
                                                            >
                                                                https://kamzavikend.si/piskotki
                                                            </a>
                                                            .
                                                        </div>
                                                        <div id="sociallogins">
                                                            <strong>7. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</strong>
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <em>In Short:&nbsp;</em>
                                                            </strong>
                                                            <em>
                                                                If you choose to register or log in to our Services
                                                                using a social media account, we may have access to
                                                                certain information about you.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            Our Services offer you the ability to register and log in
                                                            using your third-party social media account details (like
                                                            your Facebook or Twitter logins). Where you choose to do
                                                            this, we will receive certain profile information about you
                                                            from your social media provider. The profile information we
                                                            receive may vary depending on the social media provider
                                                            concerned, but will often include your name, email address,
                                                            friends list, and profile picture, as well as other
                                                            information you choose to make public on such a social media
                                                            platform.
                                                        </div>
                                                        <div>
                                                            We will use the information we receive only for the purposes
                                                            that are described in this privacy notice or that are
                                                            otherwise made clear to you on the relevant Services. Please
                                                            note that we do not control, and are not responsible for,
                                                            other uses of your personal information by your third-party
                                                            social media provider. We recommend that you review their
                                                            privacy notice to understand how they collect, use, and
                                                            share your personal information, and how you can set your
                                                            privacy preferences on their sites and apps.
                                                        </div>
                                                        <div id="inforetain">
                                                            <strong>8. HOW LONG DO WE KEEP YOUR INFORMATION?</strong>
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <em>In Short:&nbsp;</em>
                                                            </strong>
                                                            <em>
                                                                We keep your information for as long as necessary to
                                                                fulfill the purposes outlined in this privacy notice
                                                                unless otherwise required by law.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            We will only keep your personal information for as long as
                                                            it is necessary for the purposes set out in this privacy
                                                            notice, unless a longer retention period is required or
                                                            permitted by law (such as tax, accounting, or other legal
                                                            requirements). No purpose in this notice will require us
                                                            keeping your personal information for longer than the period
                                                            of time in which users have an account with us.
                                                        </div>
                                                        <div>
                                                            When we have no ongoing legitimate business need to process
                                                            your personal information, we will either delete or
                                                            anonymize such information, or, if this is not possible (for
                                                            example, because your personal information has been stored
                                                            in backup archives), then we will securely store your
                                                            personal information and isolate it from any further
                                                            processing until deletion is possible.
                                                        </div>
                                                        <div id="infosafe">
                                                            <strong>9. HOW DO WE KEEP YOUR INFORMATION SAFE?</strong>
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <em>In Short:&nbsp;</em>
                                                            </strong>
                                                            <em>
                                                                We aim to protect your personal information through a
                                                                system of organizational and technical security
                                                                measures.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            We have implemented appropriate and reasonable technical and
                                                            organizational security measures designed to protect the
                                                            security of any personal information we process. However,
                                                            despite our safeguards and efforts to secure your
                                                            information, no electronic transmission over the Internet or
                                                            information storage technology can be guaranteed to be 100%
                                                            secure, so we cannot promise or guarantee that hackers,
                                                            cybercriminals, or other unauthorized third parties will not
                                                            be able to defeat our security and improperly collect,
                                                            access, steal, or modify your information. Although we will
                                                            do our best to protect your personal information,
                                                            transmission of personal information to and from our
                                                            Services is at your own risk. You should only access the
                                                            Services within a secure environment.
                                                        </div>
                                                        <div id="infominors">
                                                            <strong>10. DO WE COLLECT INFORMATION FROM MINORS?</strong>
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <em>In Short:</em>
                                                            </strong>
                                                            <em>
                                                                &nbsp;We do not knowingly collect data from or market to
                                                                children under 18 years of age.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            We do not knowingly solicit data from or market to children
                                                            under 18 years of age. By using the Services, you represent
                                                            that you are at least 18 or that you are the parent or
                                                            guardian of such a minor and consent to such minor
                                                            dependent&rsquo;s use of the Services. If we learn that
                                                            personal information from users less than 18 years of age
                                                            has been collected, we will deactivate the account and take
                                                            reasonable measures to promptly delete such data from our
                                                            records. If you become aware of any data we may have
                                                            collected from children under age 18, please contact us at
                                                            leon@kamzavikend.si.
                                                        </div>
                                                        <div id="privacyrights">
                                                            <strong>11. WHAT ARE YOUR PRIVACY RIGHTS?</strong>
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <em>In Short:</em>
                                                            </strong>
                                                            <em>
                                                                &nbsp;In some regions, such as the European Economic
                                                                Area (EEA) and United Kingdom (UK), you have rights that
                                                                allow you greater access to and control over your
                                                                personal information.&nbsp;You may review, change, or
                                                                terminate your account at any time.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            In some regions (like the EEA and UK), you have certain
                                                            rights under applicable data protection laws. These may
                                                            include the right (i) to request access and obtain a copy of
                                                            your personal information, (ii) to request rectification or
                                                            erasure; (iii) to restrict the processing of your personal
                                                            information; and (iv) if applicable, to data portability. In
                                                            certain circumstances, you may also have the right to object
                                                            to the processing of your personal information. You can make
                                                            such a request by contacting us by using the contact details
                                                            provided in the section "
                                                            <a href="#contact" data-custom-class="link">
                                                                HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                                                            </a>
                                                            " below.
                                                        </div>
                                                        <div>
                                                            We will consider and act upon any request in accordance with
                                                            applicable data protection laws.
                                                        </div>
                                                        <div>
                                                            If you are located in the EEA or UK and you believe we are
                                                            unlawfully processing your personal information, you also
                                                            have the right to complain to your{' '}
                                                            <a
                                                                href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-custom-class="link"
                                                            >
                                                                Member State data protection authority
                                                            </a>{' '}
                                                            or&nbsp;
                                                            <a
                                                                href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                UK data protection authority
                                                            </a>
                                                            .
                                                        </div>
                                                        <div>
                                                            If you are located in Switzerland, you may contact the{' '}
                                                            <a
                                                                href="https://www.edoeb.admin.ch/edoeb/en/home.html"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-custom-class="link"
                                                            >
                                                                Federal Data Protection and Information Commissioner
                                                            </a>
                                                            .
                                                        </div>
                                                        <div id="withdrawconsent">
                                                            <strong>
                                                                <u>Withdrawing your consent:</u>
                                                            </strong>{' '}
                                                            If we are relying on your consent to process your personal
                                                            information, you have the right to withdraw your consent at
                                                            any time. You can withdraw your consent at any time by
                                                            contacting us by using the contact details provided in the
                                                            section "
                                                            <a href="#contact" data-custom-class="link">
                                                                HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                                                            </a>
                                                            " below or updating your preferences.
                                                        </div>
                                                        <div>
                                                            However, please note that this will not affect the
                                                            lawfulness of the processing before its withdrawal nor, will
                                                            it affect the processing of your personal information
                                                            conducted in reliance on lawful processing grounds other
                                                            than consent.
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <u>
                                                                    Opting out of marketing and promotional
                                                                    communications:
                                                                </u>
                                                            </strong>
                                                            You can unsubscribe from our marketing and promotional
                                                            communications at any time by clicking on the unsubscribe
                                                            link in the emails that we send, To unsubscribe you can also
                                                            write to info@kamzavikend.si, or by contacting us using the
                                                            details provided in the section "
                                                            <a href="#contact" data-custom-class="link">
                                                                HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                                                            </a>
                                                            " below. You will then be removed from the marketing lists.
                                                            However, we may still communicate with you &mdash; for
                                                            example, to send you service-related messages that are
                                                            necessary for the administration and use of your account, to
                                                            respond to service requests, or for other non-marketing
                                                            purposes.
                                                        </div>
                                                        <div>
                                                            <strong>Account Information</strong>
                                                        </div>
                                                        <div>
                                                            If you would at any time like to review or change the
                                                            information in your account or terminate your account, you
                                                            can:
                                                        </div>
                                                        <ul>
                                                            <li>
                                                                Log in to your account settings and update your user
                                                                account.
                                                            </li>
                                                        </ul>
                                                        <ul>
                                                            <li>Contact us using the contact information provided.</li>
                                                        </ul>
                                                        <ul>
                                                            <li>
                                                                To delete your account and remove all your data please
                                                                contact us on info@kamzavikend.si
                                                            </li>
                                                        </ul>
                                                        <div>
                                                            Upon your request to terminate your account, we will
                                                            deactivate or delete your account and information from our
                                                            active databases. However, we may retain some information in
                                                            our files to prevent fraud, troubleshoot problems, assist
                                                            with any investigations, enforce our legal terms and/or
                                                            comply with applicable legal requirements.
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <u>Cookies and similar technologies:</u>
                                                            </strong>{' '}
                                                            Most Web browsers are set to accept cookies by default. If
                                                            you prefer, you can usually choose to set your browser to
                                                            remove cookies and to reject cookies. If you choose to
                                                            remove cookies or reject cookies, this could affect certain
                                                            features or services of our Services. You may also{' '}
                                                            <a
                                                                href="http://www.aboutads.info/choices/"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-custom-class="link"
                                                            >
                                                                opt out of interest-based advertising by advertisers
                                                            </a>{' '}
                                                            on our Services. For further information, please see our
                                                            Cookie Notice:{' '}
                                                            <a
                                                                href="https://kamzavikend.si/piskotki"
                                                                target="_blank"
                                                                data-custom-class="link"
                                                                rel="noreferrer"
                                                            >
                                                                https://kamzavikend.si/piskotki
                                                            </a>
                                                            .
                                                        </div>
                                                        <div>
                                                            If you have questions or comments about your privacy rights,
                                                            you may email us at info@kamzavikend.si.
                                                        </div>
                                                        <div id="DNT">
                                                            <strong>12. CONTROLS FOR DO-NOT-TRACK FEATURES</strong>
                                                        </div>
                                                        <div>
                                                            Most web browsers and some mobile operating systems and
                                                            mobile applications include a Do-Not-Track ("DNT") feature
                                                            or setting you can activate to signal your privacy
                                                            preference not to have data about your online browsing
                                                            activities monitored and collected. At this stage no uniform
                                                            technology standard for recognizing and implementing DNT
                                                            signals has been finalized. As such, we do not currently
                                                            respond to DNT browser signals or any other mechanism that
                                                            automatically communicates your choice not to be tracked
                                                            online. If a standard for online tracking is adopted that we
                                                            must follow in the future, we will inform you about that
                                                            practice in a revised version of this privacy notice.
                                                        </div>
                                                        <div id="caresidents">
                                                            <strong>
                                                                13. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY
                                                                RIGHTS?
                                                            </strong>
                                                        </div>
                                                        <div>
                                                            <strong>
                                                                <em>In Short:&nbsp;</em>
                                                            </strong>
                                                            <em>
                                                                Yes, if you are a resident of California, you are
                                                                granted specific rights regarding access to your
                                                                personal information.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            California Civil Code Section 1798.83, also known as the
                                                            "Shine The Light" law, permits our users who are California
                                                            residents to request and obtain from us, once a year and
                                                            free of charge, information about categories of personal
                                                            information (if any) we disclosed to third parties for
                                                            direct marketing purposes and the names and addresses of all
                                                            third parties with which we shared personal information in
                                                            the immediately preceding calendar year. If you are a
                                                            California resident and would like to make such a request,
                                                            please submit your request in writing to us using the
                                                            contact information provided below.
                                                        </div>
                                                        <div>
                                                            If you are under 18 years of age, reside in California, and
                                                            have a registered account with Services, you have the right
                                                            to request removal of unwanted data that you publicly post
                                                            on the Services. To request removal of such data, please
                                                            contact us using the contact information provided below and
                                                            include the email address associated with your account and a
                                                            statement that you reside in California. We will make sure
                                                            the data is not publicly displayed on the Services, but
                                                            please be aware that the data may not be completely or
                                                            comprehensively removed from all our systems (e.g., backups,
                                                            etc.).
                                                        </div>
                                                        <div id="policyupdates">
                                                            <strong>14. DO WE MAKE UPDATES TO THIS NOTICE?</strong>
                                                        </div>
                                                        <div>
                                                            <em>
                                                                <strong>In Short:&nbsp;</strong>
                                                                Yes, we will update this notice as necessary to stay
                                                                compliant with relevant laws.
                                                            </em>
                                                        </div>
                                                        <div>
                                                            We may update this privacy notice from time to time. The
                                                            updated version will be indicated by an updated "Revised"
                                                            date and the updated version will be effective as soon as it
                                                            is accessible. If we make material changes to this privacy
                                                            notice, we may notify you either by prominently posting a
                                                            notice of such changes or by directly sending you a
                                                            notification. We encourage you to review this privacy notice
                                                            frequently to be informed of how we are protecting your
                                                            information.
                                                        </div>
                                                        <div id="contact">
                                                            <strong>
                                                                15. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                                                            </strong>
                                                        </div>
                                                        <div>
                                                            If you have questions or comments about this notice, you may
                                                            contact our Data Protection Officer (DPO) , Leon
                                                            &Scaron;krilec,&nbsp;by email
                                                            at&nbsp;leon@kamzavikend.si,&nbsp;by phone at +386 40 846
                                                            488,&nbsp;or contact us by post at:
                                                        </div>
                                                        <div>HUDDA d.o.o.</div>
                                                        <div>Trg komandanta Staneta 8</div>
                                                        <div>1000 Ljubljana</div>
                                                        <div>Slovenia</div>
                                                        <div id="request">
                                                            <strong>
                                                                16. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE
                                                                COLLECT FROM YOU?
                                                            </strong>
                                                        </div>
                                                        <div>
                                                            Based on the applicable laws of your country, you may have
                                                            the right to request access to the personal information we
                                                            collect from you, change that information, or delete it. To
                                                            request to review, update, or delete your personal
                                                            information, please fill out and submit a&nbsp;
                                                            <a
                                                                href="https://app.termly.io/notify/9e0e0db1-8123-425f-a1b2-0296e0251163"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-custom-class="link"
                                                            >
                                                                data subject access request
                                                            </a>
                                                            .
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
